import type { SizeProp } from '@fortawesome/fontawesome-svg-core';
import { faBullhorn } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';
import { Link } from 'react-router-dom';

import type { CampaignClass } from '@feathr/blackbox';
import { Chip, EFontAwesomeKitIcon, FontAwesomeKitIcon } from '@feathr/components';
import { useStore } from '@feathr/report_components/state';
import {
  campaignColorMap,
  campaignIconMap,
  customCampaignIconMap,
} from '@feathr/report_components/styles/campaign';

interface IProps {
  id: string;
  localUrl?: (url: string) => string;
}

export function getCampaignIcon(
  campaignClass: CampaignClass,
  size?: SizeProp,
  className?: string,
): JSX.Element | null {
  if (customCampaignIconMap.has(campaignClass)) {
    return (
      <FontAwesomeKitIcon
        className={className}
        data-name={'campaign-type-icon'}
        iconName={customCampaignIconMap.get(campaignClass) ?? EFontAwesomeKitIcon.feathr}
        size={size}
      />
    );
  }
  return (
    <FontAwesomeIcon
      className={className}
      data-name={'campaign-type-icon'}
      icon={campaignIconMap.get(campaignClass) ?? faBullhorn}
      size={size}
    />
  );
}

function CampaignChip({ id, localUrl }: Readonly<IProps>): JSX.Element | null {
  const { Campaigns } = useStore();

  const campaign = Campaigns.get(id);
  if (campaign.isErrored) {
    return null;
  }

  if (campaign.isPending) {
    return <Chip isLoading={true} />;
  }

  const type = campaign.get('_cls');
  const content = (
    <Chip prefix={getCampaignIcon(type)} theme={campaignColorMap.get(type)}>
      {campaign.name}
      {campaign.get('is_archived') ? ' (Archived)' : ''}
    </Chip>
  );

  if (localUrl) {
    return <Link to={localUrl(campaign.getItemUrl())}>{content}</Link>;
  }
  return content;
}

export default observer(CampaignChip);
